import { useEffect, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import Modal from '@/components/base/Modal';
import { useAuth } from '@/context/AuthContext';
import { triggerUrlDownload, revokeObjectUrl } from '@/lib/download';
import RestoreBackupSection from '@/pages/admin/reinicio/components/RestoreBackupSection';
import {
  RESET_SCOPES,
  runReset,
  previewResetCounts,
  buildBackup,
  createBackupFile,
  type BackupFile,
  type ResetScope,
  type ResetResult,
  type ResetPreview,
} from '@/services/reset';

type Feedback =
  | { type: 'success'; msg: string }
  | { type: 'error'; msg: string }
  | null;

const EMPTY_PREVIEW: ResetPreview = { counts: {}, abandoned: { orders: 0, units: 0 } };

export default function ResetDataPage() {
  const { profile, profileLoading, user } = useAuth();
  const isAdmin = profile?.role === 'admin';

  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFile, setBackupFile] = useState<BackupFile | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const [preview, setPreview] = useState<ResetPreview>(EMPTY_PREVIEW);
  const [previewLoading, setPreviewLoading] = useState(true);

  const [target, setTarget] = useState<ResetScope | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [releaseReservations, setReleaseReservations] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  const CONFIRM_WORD = 'REINICIAR';

  async function loadPreview() {
    setPreviewLoading(true);
    const data = await previewResetCounts();
    setPreview(data);
    setPreviewLoading(false);
  }

  useEffect(() => {
    if (!isAdmin) return;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Libera la URL temporal del respaldo cuando se reemplaza por uno nuevo o
  // cuando se desmonta la página, para no dejar memoria colgando.
  useEffect(() => {
    return () => {
      if (backupFile?.url) revokeObjectUrl(backupFile.url);
    };
  }, [backupFile]);

  function openTarget(scope: ResetScope) {
    setTarget(scope);
    setConfirmText('');
    setResetError('');
    setReleaseReservations(true);
  }

  async function handleBackup() {
    setBackupLoading(true);
    setFeedback(null);
    try {
      const result = await buildBackup();
      if (result.totalRows === 0 && result.errors.length > 0) {
        setFeedback({ type: 'error', msg: `No se pudo generar el respaldo. ${result.errors[0]}` });
        return;
      }
      const file = createBackupFile(result.data, user?.email ?? profile?.fullName ?? undefined);
      setBackupFile(file);
      // Descarga automática (puede ser bloqueada por el navegador en la vista previa).
      triggerUrlDownload(file.url, file.filename);
      const sizeKb = Math.max(1, Math.round(file.sizeBytes / 1024));
      if (result.errors.length > 0) {
        setFeedback({
          type: 'error',
          msg: `Respaldo generado ("${file.filename}", ${sizeKb} KB), pero algunas tablas no se pudieron incluir: ${result.errors.join(' · ')}`,
        });
      } else {
        setFeedback({
          type: 'success',
          msg: `Respaldo listo: "${file.filename}" (${file.totalRows.toLocaleString()} registros · ${sizeKb} KB). Si el navegador no lo descarga solo, usa el botón de abajo para guardarlo.`,
        });
      }
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo generar el respaldo.' });
    } finally {
      setBackupLoading(false);
    }
  }

  async function handleConfirmReset() {
    if (!target || confirmText.trim().toUpperCase() !== CONFIRM_WORD) return;
    setResetting(true);
    setResetError('');
    const result: ResetResult = await runReset(target.id, {
      releaseReservations: target.id === 'sales' ? releaseReservations : false,
    });
    setResetting(false);
    if (!result.success) {
      setResetError(result.error ?? 'No se pudo completar el reinicio.');
      return;
    }
    const total = (result.results ?? []).reduce(
      (sum, r) => sum + (r.deleted ?? 0) + (r.updated ?? 0),
      0,
    );
    setTarget(null);
    setFeedback({
      type: 'success',
      msg: `Área "${target.label}" reiniciada correctamente (${total.toLocaleString()} registros afectados).`,
    });
    loadPreview();
  }

  function countFor(scopeId: string): number {
    return preview.counts[scopeId] ?? 0;
  }

  if (profileLoading) {
    return (
      <AdminLayout>
        <div className="p-6 text-center py-20 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Verificando permisos...
        </div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-4 lg:p-6 max-w-[900px] mx-auto">
          <div className="bg-white rounded-xl border border-background-200 p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-2-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-950">Acceso restringido</h2>
            <p className="text-sm text-foreground-500 mt-1.5">
              Solo un administrador puede acceder al reinicio de datos.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const targetRecords = target ? countFor(target.id) : 0;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1100px] mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Reinicio de datos</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Vacía áreas específicas para arrancar de ceros con datos reales. La configuración y el catálogo se conservan.
            </p>
          </div>
          <button
            onClick={loadPreview}
            disabled={previewLoading}
            className="text-sm px-4 py-2 bg-white border border-background-300 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60"
          >
            {previewLoading ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Contando...
              </>
            ) : (
              <>
                <i className="ri-refresh-line mr-1.5"></i>Actualizar resumen
              </>
            )}
          </button>
        </div>

        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <i className="ri-alert-line text-amber-600 text-lg mt-0.5"></i>
          <div>
            <p className="text-sm font-semibold text-amber-900">Esta acción es irreversible</p>
            <p className="text-sm text-amber-800 mt-0.5">
              Te recomendamos <strong>descargar una copia de seguridad</strong> antes de reiniciar cualquier área.
              El reinicio no se puede deshacer.
            </p>
          </div>
        </div>

        {feedback && (
          <div
            className={`flex items-start gap-2 text-sm rounded-xl px-4 py-3 border ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            <i
              className={`${
                feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'
              } mt-0.5`}
            ></i>
            <span>{feedback.msg}</span>
          </div>
        )}

        {/* Copia de seguridad */}
        <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center">
              <i className="ri-download-cloud-2-line text-secondary-600"></i>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground-900">Copia de seguridad</h2>
              <p className="text-xs text-foreground-500">
                Descarga un archivo JSON con todos tus datos de negocio antes de reiniciar.
              </p>
            </div>
          </div>
          <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-foreground-600">
              Incluye productos, inventario, clientes, ventas, pagos, compras, donaciones y más. No incluye
              credenciales ni configuración.
            </p>
            <button
              onClick={handleBackup}
              disabled={backupLoading}
              className="text-sm px-5 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
            >
              {backupLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1.5"></i>Generando respaldo...
                </>
              ) : (
                <>
                  <i className="ri-download-line mr-1.5"></i>Descargar respaldo
                </>
              )}
            </button>
          </div>
          {backupFile && (
            <div className="px-5 pb-5">
              <div className="rounded-lg border border-secondary-200 bg-secondary-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-foreground-600 leading-relaxed">
                  ¿El navegador no inició la descarga? Guarda tu respaldo con el botón de aquí.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={backupFile.url}
                    download={backupFile.filename}
                    className="inline-flex items-center gap-1.5 rounded-md bg-secondary-600 hover:bg-secondary-700 text-background-50 text-xs font-semibold px-3 py-1.5 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-line"></i>
                    Guardar respaldo
                  </a>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Restaurar respaldo */}
        <RestoreBackupSection />

        {/* Áreas de reinicio */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground-900">Áreas de reinicio</h2>
            <span className="text-xs text-foreground-400">
              {previewLoading ? 'Contando registros...' : 'Resumen actual de registros por área'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {RESET_SCOPES.map((scope) => {
              const count = countFor(scope.id);
              return (
                <div
                  key={scope.id}
                  className="bg-white rounded-xl border border-background-200 p-4 flex flex-col"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <i className={`${scope.icon} text-red-500 text-lg`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground-950">{scope.label}</h3>
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                            count > 0
                              ? 'bg-red-50 text-red-600'
                              : 'bg-background-100 text-foreground-400'
                          }`}
                        >
                          {previewLoading ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            `${count.toLocaleString()} ${count === 1 ? 'registro' : 'registros'}`
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{scope.description}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-foreground-400 leading-snug">{scope.affects}</span>
                    <button
                      onClick={() => openTarget(scope)}
                      className="text-xs px-3.5 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap font-semibold cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-restart-line mr-1"></i>Reiniciar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {target && (
        <Modal
          title={`Reiniciar: ${target.label}`}
          subtitle="Esta acción es permanente y no se puede deshacer."
          onClose={() => !resetting && setTarget(null)}
          maxWidth="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => !resetting && setTarget(null)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmReset}
                disabled={resetting || confirmText.trim().toUpperCase() !== CONFIRM_WORD}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Reiniciando...
                  </>
                ) : (
                  <>
                    <i className="ri-restart-line mr-1.5"></i>Reiniciar ahora
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <i className="ri-error-warning-line text-red-600 mt-0.5"></i>
              <p className="text-sm text-red-800">
                Se borrarán permanentemente: <strong>{target.affects}</strong>. No se puede deshacer.
              </p>
            </div>

            {/* Resumen previo de lo que se va a vaciar */}
            <div className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 flex items-center justify-between gap-3">
              <span className="text-sm text-foreground-600">Registros a vaciar en esta área</span>
              <span className="text-sm font-bold text-red-600 whitespace-nowrap">
                {previewLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  targetRecords.toLocaleString()
                )}
              </span>
            </div>

            {/* Opción exclusiva de Ventas y pedidos */}
            {target.id === 'sales' && (
              <label className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50/60 px-3 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={releaseReservations}
                  onChange={(e) => setReleaseReservations(e.target.checked)}
                  disabled={resetting}
                  className="mt-0.5 w-4 h-4 accent-secondary-500 flex-shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground-900">
                    Devolver al inventario el stock reservado por pedidos abandonados
                  </span>
                  <span className="block text-xs text-foreground-600 mt-0.5 leading-relaxed">
                    {preview.abandoned.orders > 0 ? (
                      <>
                        Hay <strong>{preview.abandoned.orders.toLocaleString()}</strong> pedido(s) abandonado(s) que
                        retienen <strong>{preview.abandoned.units.toLocaleString()}</strong> unidad(es). Se devolverán
                        al stock disponible antes de borrarlos.
                      </>
                    ) : (
                      'No hay pedidos abandonados con stock reservado en este momento. No habría cambios en el inventario.'
                    )}
                  </span>
                </span>
              </label>
            )}

            {resetError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{resetError}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Escribe <strong>{CONFIRM_WORD}</strong> para confirmar
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                disabled={resetting}
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}